import { useEffect, useRef, useCallback } from 'react';
import useAuthStore from '../stores/authStore';
import authService from '../services/authService';

/**
 * Decode a JWT payload without verification (client-side only).
 * We only need the `exp` claim to schedule proactive refresh.
 */
function decodeJwtExpiry(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(json);
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch {
    return null;
  }
}

// Attempt refresh 2 minutes before token expires
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

// BroadcastChannel shared with useApi.js for cross-tab token sync.
// When this tab refreshes proactively, it broadcasts the new token so
// other open tabs cancel their own timers and don't hit the rate limit.
const refreshChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('craftflow_token_refresh') : null;

/**
 * Monitors the current JWT token and proactively refreshes it before expiry.
 * If refresh fails, logs the user out and redirects to /login?expired=1.
 *
 * Must be called inside a React component that renders while authenticated
 * (e.g. ProtectedRoute).
 */
export function useSessionMonitor() {
  const timerRef = useRef(null);
  const userToken = useAuthStore(state => state.userToken);
  const isUserAuthenticated = useAuthStore(state => state.isUserAuthenticated);

  const handleSessionExpired = useCallback(() => {
    authService.logoutUser();
    window.location.href = '/login?expired=1';
  }, []);

  const attemptRefresh = useCallback(async () => {
    try {
      const result = await authService.refreshJwtToken();
      if (!result.success) {
        // 429 rate-limit: another tab already refreshed — don't log out,
        // the BroadcastChannel listener below will update our token shortly.
        if (result.error && result.error.includes('429')) {
          return;
        }
        handleSessionExpired();
      } else {
        // Broadcast the new token to all other open tabs so they cancel
        // their own pending refresh timers and don't redundantly hit the API.
        if (refreshChannel) {
          refreshChannel.postMessage({
            type: 'TOKEN_REFRESHED',
            token: result.access_token,
          });
        }
        // On success the store updates with the new token, which re-triggers
        // this effect and schedules a fresh timer automatically.
      }
    } catch {
      handleSessionExpired();
    }
  }, [handleSessionExpired]);

  // Listen for token refreshes from other tabs. When another tab refreshes
  // proactively, update our store so our useEffect re-runs with the new
  // token and cancels the current timer (preventing a duplicate refresh call).
  useEffect(() => {
    if (!refreshChannel) return;

    const handleMessage = event => {
      if (event.data?.type === 'TOKEN_REFRESHED' && event.data.token) {
        useAuthStore.getState().setUserAuth(useAuthStore.getState().user, event.data.token);
      }
    };

    refreshChannel.addEventListener('message', handleMessage);
    return () => refreshChannel.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!isUserAuthenticated || !userToken) return;

    const expiresAtMs = decodeJwtExpiry(userToken);
    if (!expiresAtMs) return;

    const now = Date.now();
    const timeUntilExpiry = expiresAtMs - now;

    // Token already expired — try to refresh immediately
    if (timeUntilExpiry <= 0) {
      attemptRefresh();
      return;
    }

    // Schedule a proactive refresh before the token expires
    const refreshIn = Math.max(timeUntilExpiry - REFRESH_BUFFER_MS, 0);

    timerRef.current = setTimeout(() => {
      attemptRefresh();
    }, refreshIn);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [userToken, isUserAuthenticated, attemptRefresh]);
}

export default useSessionMonitor;
